<template>
  <div class="wd-form">
    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:domain" width="16" color="#888" />
        <span>Company</span>
        <div v-if="getSectionDocNames('company').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('company').slice(0, MAX_DOC_CHIPS)"
            :key="'company-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('company').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('company').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="model.companyName"
            label="Company name *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:card-account-details-outline" width="16" color="#888" />
        <span>Company Registration</span>
        <div v-if="getSectionDocNames('companyRegistration').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('companyRegistration').slice(0, MAX_DOC_CHIPS)"
            :key="'companyRegistration-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('companyRegistration').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('companyRegistration').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="model.companyRegistrationNo"
            label="Company Registration No. *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:map-marker-outline" width="16" color="#888" />
        <span>Company Address</span>
        <div v-if="getSectionDocNames('companyAddress').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('companyAddress').slice(0, MAX_DOC_CHIPS)"
            :key="'companyAddress-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('companyAddress').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('companyAddress').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="model.companyAddress"
            label="Company address *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:account-remove" width="16" color="#888" />
        <span>Withdraw Secretary</span>
        <div v-if="getSectionDocNames('withdrawSecretary').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('withdrawSecretary').slice(0, MAX_DOC_CHIPS)"
            :key="'withdraw-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('withdrawSecretary').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('withdrawSecretary').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <div v-if="withdrawSecretaryRepeatingSections.length" class="wd-repeating-wrap">
        <v-progress-linear
          v-if="isLoadingRepeatingSections"
          indeterminate
          color="primary"
          height="2"
          class="mb-3"
        />
        <div class="wd-repeating-toolbar">
          <v-btn
            variant="text"
            size="small"
            color="primary"
            :disabled="!!readOnly"
            @click="requestLookupForResigningSecretary"
          >
            <v-icon start size="16">mdi-creation</v-icon>
            Lookup secretary from chat
          </v-btn>
        </div>
        <RepeatingSectionTable
          v-for="sec in withdrawSecretaryRepeatingSections"
          :key="sec.sectionName"
          class="wd-repeating-table"
          :section-name="sec.sectionName"
          :columns="sec.columns"
          :rows="getRepeatingRows(sec.sectionName)"
          @update-cell="(rowIdx, varKey, val) => onRepeatingCellUpdate(sec.sectionName, rowIdx, varKey, val)"
          @update-header="(colIdx, header) => onRepeatingHeaderUpdate(sec.sectionName, colIdx, header)"
          @add-row="onRepeatingAddRow(sec.sectionName)"
          @remove-row="(rowIdx) => onRepeatingRemoveRow(sec.sectionName, rowIdx)"
          @add-column="onRepeatingAddColumn(sec.sectionName)"
          @remove-column="(colIdx) => onRepeatingRemoveColumn(sec.sectionName, colIdx)"
          @field-click="(rowIdx, varKey) => onRepeatingFieldClick(sec.sectionName, rowIdx, varKey)"
        />
      </div>
      <v-row v-else dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.secretaryName"
            label="Secretary name *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          >
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestLookupForResigningSecretary"
                  />
                </template>
                <span>Lookup secretary from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.secretaryIdentificationNo"
            label="Secretary identification no. *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:email-edit-outline" width="16" color="#888" />
        <span>Resignation Details</span>
        <div v-if="getSectionDocNames('resignationDetails').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('resignationDetails').slice(0, MAX_DOC_CHIPS)"
            :key="'resignationDetails-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('resignationDetails').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('resignationDetails').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-menu
            v-model="resignationMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="resignationDisplay"
                :placeholder="resignationPlaceholder"
                label="Resignation date *"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
              />
            </template>
            <v-date-picker
              v-bind="resignationPickerProps"
              v-model="resignationPickerValue"
              @update:model-value="resignationMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="
              suggestedToday &&
              model.resignationDate !== suggestedToday &&
              !isSuggestionDismissed('resignationDate', suggestedToday)
            "
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 wd-suggest-chip"
            @click.stop="applySuggestedToday('resignationDate')"
            @click:close="dismissSuggestion('resignationDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="model.secretaryResignationAddress"
            label="Resigning secretary address *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:gavel" width="16" color="#888" />
        <span>Resolution</span>
        <div v-if="getSectionDocNames('resolution').length" class="wd-section-docs">
          <span class="wd-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in getSectionDocNames('resolution').slice(0, MAX_DOC_CHIPS)"
            :key="'resolution-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="wd-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="getSectionDocNames('resolution').length > MAX_DOC_CHIPS" class="wd-more-chip">
            +{{ getSectionDocNames('resolution').length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-menu
            v-model="resolutionMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="resolutionDisplay"
                :placeholder="resolutionPlaceholder"
                label="Resolution date *"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
              />
            </template>
            <v-date-picker
              v-bind="resolutionPickerProps"
              v-model="resolutionPickerValue"
              @update:model-value="resolutionMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="
              suggestedToday &&
              model.resolutionDate !== suggestedToday &&
              !isSuggestionDismissed('resolutionDate', suggestedToday)
            "
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 wd-suggest-chip"
            @click.stop="applySuggestedToday('resolutionDate')"
            @click:close="dismissSuggestion('resolutionDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
    </div>

    <div class="wd-section">
      <div class="wd-section-header">
        <Icon icon="mdi:file-document-edit-outline" width="16" color="#888" />
        <span>E-Filing Details</span>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-select
            v-model="model.reasonForCessation"
            label="Reason for cessation *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :items="reasonOptions"
            :loading="isLoadingReasons"
            clearable
          />
        </v-col>
      </v-row>
      <v-row v-if="model.reasonForCessation === 'B12004'" dense>
        <v-col cols="12">
          <v-textarea
            v-model="model.otherReasonForCessation"
            label="Other reason *"
            variant="outlined"
            density="compact"
            hide-details="auto"
            rows="3"
            :readonly="!!readOnly"
            placeholder="Please enter other reason"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import RepeatingSectionTable from '@/features/AI-documents/components/RepeatingSectionTable.vue';
import {
  createCustomRepeatingColumn,
  isCustomRepeatingColumn,
} from '@/features/AI-documents/utils/repeatingSectionColumns';
import type {
  OfficerDetail,
  RepeatingSectionColumn,
  RepeatingSectionRowData,
} from '@/features/AI-documents/types/docGen.types';
import type { WithdrawSecretaryFormModel } from './composables/useFormPayload';

export interface WithdrawSecretaryPresetDocument {
  templateName: string;
  docCode: string;
  tmpId?: number | null;
  templateId?: number | null;
}

interface WithdrawSecretaryRepeatingSectionMeta {
  sectionName: string;
  columns: RepeatingSectionColumn[];
}

const MAX_DOC_CHIPS = 3;
const DEFAULT_DOCS = ['DR (Withdraw Secretary).docx', 'Resignation Letter (Secretary).docx'];

const props = withDefaults(
  defineProps<{
    readOnly?: boolean;
    documents?: WithdrawSecretaryPresetDocument[];
    selectedPerson?: OfficerDetail | null;
    activePersonContext?: Record<string, any> | null;
  }>(),
  {
    readOnly: false,
    documents: () => [],
    selectedPerson: null,
    activePersonContext: null,
  },
);

const model = defineModel<WithdrawSecretaryFormModel>({ required: true });
const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, any>): void;
}>();

const formatDateByUserFormat = useFormatDateByUserFormat();
const repeatingSectionsMeta = ref<WithdrawSecretaryRepeatingSectionMeta[]>([]);
const isLoadingRepeatingSections = ref(false);

function normalizeIso(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function normalizeSectionName(name: unknown): string {
  return String(name || '').trim().toLowerCase();
}

function isWithdrawSecretaryRepeatingSection(section: WithdrawSecretaryRepeatingSectionMeta): boolean {
  const name = normalizeSectionName(section.sectionName);
  if (/withdraw|resign|secretary/.test(name)) return true;
  return section.columns.some((col) =>
    /secretary|name|identification|id_no|nric|passport/i.test(col.varKey),
  );
}

function formatSectionHeader(varKey: string): string {
  return String(varKey || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildEmptyRepeatingRow(columns: RepeatingSectionColumn[]): RepeatingSectionRowData {
  const row: RepeatingSectionRowData = {};
  for (const col of columns) row[col.varKey] = '';
  return row;
}

function buildInitialRepeatingRow(section: WithdrawSecretaryRepeatingSectionMeta): RepeatingSectionRowData {
  const row = buildEmptyRepeatingRow(section.columns);
  const nameKey = section.columns.find((col) => /name/i.test(col.varKey))?.varKey;
  if (nameKey && model.value.secretaryName) {
    row[nameKey] = model.value.secretaryName;
  }
  const idKey = section.columns.find((col) =>
    /identification|id_no|nric|passport|reg_no|identity/i.test(col.varKey),
  )?.varKey;
  if (idKey && model.value.secretaryIdentificationNo) {
    row[idKey] = model.value.secretaryIdentificationNo;
  }
  return row;
}

function mergeRepeatingSectionMeta(
  sections: WithdrawSecretaryRepeatingSectionMeta[],
): WithdrawSecretaryRepeatingSectionMeta[] {
  const merged: WithdrawSecretaryRepeatingSectionMeta[] = [];

  for (const rawSection of sections) {
    if (!rawSection?.sectionName) continue;
    const normalizedSectionName = normalizeSectionName(rawSection.sectionName);
    const existing = merged.find((section) => normalizeSectionName(section.sectionName) === normalizedSectionName);
    if (!existing) {
      merged.push({
        sectionName: rawSection.sectionName,
        columns: (rawSection.columns || []).map((col) => ({ ...col })),
      });
      continue;
    }
    for (const rawCol of rawSection.columns || []) {
      if (!existing.columns.some((col) => col.varKey === rawCol.varKey)) {
        existing.columns.push({ ...rawCol });
      }
    }
  }

  return merged.filter(isWithdrawSecretaryRepeatingSection);
}

function deriveRepeatingSectionMetaFromRows(): WithdrawSecretaryRepeatingSectionMeta[] {
  const sections: WithdrawSecretaryRepeatingSectionMeta[] = [];
  const repeatingData = model.value.repeatingData || {};

  for (const [sectionName, rows] of Object.entries(repeatingData)) {
    if (!Array.isArray(rows) || rows.length === 0) continue;
    const keys = Array.from(
      new Set(rows.flatMap((row) => (row && typeof row === 'object' ? Object.keys(row) : []))),
    );
    if (keys.length === 0) continue;
    sections.push({
      sectionName,
      columns: keys.map((key) => ({
        varKey: key,
        header: formatSectionHeader(key),
        varLabel: key,
      })),
    });
  }

  return sections;
}

function replaceRepeatingSectionMeta(nextSections: WithdrawSecretaryRepeatingSectionMeta[]) {
  repeatingSectionsMeta.value = mergeRepeatingSectionMeta(nextSections);
}

const withdrawSecretaryRepeatingSections = computed(() => repeatingSectionsMeta.value);

function getRepeatingRows(sectionName: string): RepeatingSectionRowData[] {
  return model.value.repeatingData?.[sectionName] || [];
}

function findPrimarySecretaryRows(): RepeatingSectionRowData[] {
  const exact = withdrawSecretaryRepeatingSections.value.find(
    (section) => normalizeSectionName(section.sectionName) === 'resign_secretaries',
  );
  if (exact) return getRepeatingRows(exact.sectionName);

  const fallback = withdrawSecretaryRepeatingSections.value.find((section) =>
    /resign|secretary/.test(normalizeSectionName(section.sectionName)),
  );
  return fallback ? getRepeatingRows(fallback.sectionName) : [];
}

function extractSecretaryNameFromRows(rows: RepeatingSectionRowData[]): string {
  for (const row of rows) {
    const candidate = String(
      row.secretary_name ?? row.name ?? row.director_name ?? '',
    ).trim();
    if (candidate) return candidate;
  }
  return '';
}

function extractSecretaryIdFromRows(rows: RepeatingSectionRowData[]): string {
  for (const row of rows) {
    const candidate = String(
      row.secretary_identification_no ??
        row.identification_no ??
        row.nric_no ??
        row.passport_no ??
        '',
    ).trim();
    if (candidate) return candidate;
  }
  return '';
}

function syncSecretaryFlatFieldsFromRepeatingRows() {
  if (!withdrawSecretaryRepeatingSections.value.length) return;
  const rows = findPrimarySecretaryRows();
  const name = extractSecretaryNameFromRows(rows);
  const id = extractSecretaryIdFromRows(rows);
  if (name) model.value.secretaryName = name;
  if (id) model.value.secretaryIdentificationNo = id;
}

function setRepeatingRows(sectionName: string, rows: RepeatingSectionRowData[]) {
  model.value.repeatingData = {
    ...(model.value.repeatingData || {}),
    [sectionName]: rows,
  };
  syncSecretaryFlatFieldsFromRepeatingRows();
}

function onRepeatingCellUpdate(sectionName: string, rowIdx: number, varKey: string, value: string) {
  const rows = [...getRepeatingRows(sectionName)];
  if (!rows[rowIdx]) {
    const section = withdrawSecretaryRepeatingSections.value.find((item) => item.sectionName === sectionName);
    rows[rowIdx] = buildEmptyRepeatingRow(section?.columns || []);
  }
  rows[rowIdx] = { ...rows[rowIdx], [varKey]: value };
  setRepeatingRows(sectionName, rows);
}

function onRepeatingHeaderUpdate(sectionName: string, colIdx: number, newHeader: string) {
  const section = repeatingSectionsMeta.value.find((item) => item.sectionName === sectionName);
  if (!section?.columns[colIdx]) return;
  section.columns[colIdx].header = newHeader;
}

function onRepeatingAddRow(sectionName: string) {
  const section = withdrawSecretaryRepeatingSections.value.find((item) => item.sectionName === sectionName);
  if (!section) return;
  setRepeatingRows(sectionName, [...getRepeatingRows(sectionName), buildEmptyRepeatingRow(section.columns)]);
}

function onRepeatingRemoveRow(sectionName: string, rowIdx: number) {
  setRepeatingRows(
    sectionName,
    getRepeatingRows(sectionName).filter((_, index) => index !== rowIdx),
  );
}

function onRepeatingAddColumn(sectionName: string) {
  const section = repeatingSectionsMeta.value.find((item) => item.sectionName === sectionName);
  if (!section) return;
  const newCol = createCustomRepeatingColumn(section.columns);
  section.columns.push(newCol);
  const rows = getRepeatingRows(sectionName).map((row) => ({ ...row, [newCol.varKey]: row[newCol.varKey] || '' }));
  setRepeatingRows(sectionName, rows.length > 0 ? rows : [buildEmptyRepeatingRow(section.columns)]);
}

function onRepeatingRemoveColumn(sectionName: string, colIdx: number) {
  const section = repeatingSectionsMeta.value.find((item) => item.sectionName === sectionName);
  if (!section?.columns[colIdx]) return;
  if (!isCustomRepeatingColumn(section.columns[colIdx])) return;
  const removedKey = section.columns[colIdx].varKey;
  section.columns.splice(colIdx, 1);
  const rows = getRepeatingRows(sectionName).map((row) => {
    const nextRow = { ...row };
    delete nextRow[removedKey];
    return nextRow;
  });
  setRepeatingRows(sectionName, rows);
}

function onRepeatingFieldClick(sectionName: string, rowIdx: number, varKey: string) {
  if (!/name|secretary/i.test(varKey)) return;
  emit('request-person-lookup', {
    fieldGroup: 'resigningSecretary',
    defaultRole: 'SECRETARY',
    sectionName,
    rowIndex: rowIdx,
  });
}

function applySelectedPersonToRepeatingSection(
  person: OfficerDetail,
  ctx: Record<string, any> | null | undefined,
) {
  if (!withdrawSecretaryRepeatingSections.value.length) return;
  const targetSectionName = String(
    ctx?.sectionName || withdrawSecretaryRepeatingSections.value[0]?.sectionName || '',
  ).trim();
  if (!targetSectionName) return;

  const section = withdrawSecretaryRepeatingSections.value.find(
    (item) => item.sectionName === targetSectionName,
  );
  if (!section) return;

  const rowIdx = Number.isInteger(ctx?.rowIndex) ? Number(ctx?.rowIndex) : 0;
  const rows = [...getRepeatingRows(section.sectionName)];
  if (!rows[rowIdx]) rows[rowIdx] = buildEmptyRepeatingRow(section.columns);

  const nextRow = { ...rows[rowIdx] };
  const nameKey = section.columns.find((col) => /name/i.test(col.varKey))?.varKey;
  const idKey = section.columns.find((col) =>
    /identification|id_no|nric|passport|reg_no|identity/i.test(col.varKey),
  )?.varKey;
  const fullName = String(person.name || person.contactName || '').trim();
  const idValue = String(
    (person as any).nricNo || (person as any).passportNo || person.contactRegNo || '',
  ).trim();

  if (nameKey && fullName) nextRow[nameKey] = fullName;
  if (idKey && idValue) nextRow[idKey] = idValue;

  rows[rowIdx] = nextRow;
  setRepeatingRows(section.sectionName, rows);
}

async function fetchRepeatingSectionsFromDocuments() {
  const docsWithTemplateId = (props.documents || []).filter((doc) => (doc.tmpId ?? doc.templateId));
  if (docsWithTemplateId.length === 0) {
    replaceRepeatingSectionMeta(deriveRepeatingSectionMetaFromRows());
    return;
  }

  isLoadingRepeatingSections.value = true;
  try {
    const sectionLists = await Promise.all(
      docsWithTemplateId.map(async (doc) => {
        const templateId = doc.tmpId ?? doc.templateId;
        const res = await fetch('/api/aidocuments/extract-variables', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ templateId }),
        });
        const json = await res.json();
        return Array.isArray(json?.repeatingSections) ? json.repeatingSections : [];
      }),
    );

    replaceRepeatingSectionMeta([
      ...sectionLists.flat().map((section: any) => ({
        sectionName: section.sectionName,
        columns: Array.isArray(section.columns) ? section.columns : [],
      })),
      ...deriveRepeatingSectionMetaFromRows(),
    ]);
  } catch (error) {
    console.warn('[WithdrawSecretaryForm] failed to load repeating sections', error);
    replaceRepeatingSectionMeta(deriveRepeatingSectionMetaFromRows());
  } finally {
    isLoadingRepeatingSections.value = false;
  }
}

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(
    get,
    (v) => {
      const s = normalizeIso(v);
      if (field.dateValue.value !== s) field.dateValue.value = s;
    },
    { immediate: true },
  );
  watch(field.dateValue, (v) => {
    const s = normalizeIso(v);
    if (normalizeIso(get()) !== s) set(s);
  });
}

const resignation = useDateField();
const resolution = useDateField();

bindModelDate(
  () => model.value.resignationDate,
  (v) => {
    model.value.resignationDate = v;
  },
  resignation,
);
bindModelDate(
  () => model.value.resolutionDate,
  (v) => {
    model.value.resolutionDate = v;
  },
  resolution,
);

const resignationMenu = resignation.dateMenu;
const resignationDisplay = resignation.dateDisplay;
const resignationPickerValue = resignation.datePickerValue;
const resignationPlaceholder = resignation.datePlaceholder;
const resignationPickerProps = resignation.datePickerProps;

const resolutionMenu = resolution.dateMenu;
const resolutionDisplay = resolution.dateDisplay;
const resolutionPickerValue = resolution.datePickerValue;
const resolutionPlaceholder = resolution.datePlaceholder;
const resolutionPickerProps = resolution.datePickerProps;

const docList = computed(() => {
  const names = (props.documents || [])
    .map((d) => String(d.templateName || '').trim())
    .filter(Boolean);
  return names.length ? names : DEFAULT_DOCS;
});

type WithdrawSecretaryFieldKey =
  | 'companyName'
  | 'companyRegistrationNo'
  | 'companyAddress'
  | 'secretaryName'
  | 'secretaryIdentificationNo'
  | 'resignationDate'
  | 'secretaryResignationAddress'
  | 'resolutionDate';

const fieldDocMap = computed<Record<WithdrawSecretaryFieldKey, string[]>>(() => {
  const init: Record<WithdrawSecretaryFieldKey, string[]> = {
    companyName: [],
    companyRegistrationNo: [],
    companyAddress: [],
    secretaryName: [],
    secretaryIdentificationNo: [],
    resignationDate: [],
    secretaryResignationAddress: [],
    resolutionDate: [],
  };

  for (const rawName of docList.value) {
    const name = String(rawName || '').trim();
    const lower = name.toLowerCase();
    const isLetter = lower.includes('resignation') && lower.includes('secretary');
    const isDr =
      !isLetter &&
      lower.includes('secretary') &&
      (lower.includes('withdraw') || lower.includes('dr'));

    if (isDr) {
      init.companyName.push(name);
      init.companyRegistrationNo.push(name);
      init.secretaryName.push(name);
      init.secretaryIdentificationNo.push(name);
      init.resolutionDate.push(name);
    }
    if (isLetter) {
      init.companyName.push(name);
      init.companyAddress.push(name);
      init.secretaryName.push(name);
      init.resignationDate.push(name);
      init.secretaryResignationAddress.push(name);
    }
  }

  (Object.keys(init) as WithdrawSecretaryFieldKey[]).forEach((k) => {
    init[k] = Array.from(new Set(init[k]));
  });

  return init;
});

function getFieldDocNames(field: WithdrawSecretaryFieldKey): string[] {
  return fieldDocMap.value[field] || [];
}

type WithdrawSecretarySectionKey =
  | 'company'
  | 'companyRegistration'
  | 'companyAddress'
  | 'withdrawSecretary'
  | 'resignationDetails'
  | 'resolution';

function getSectionDocNames(section: WithdrawSecretarySectionKey): string[] {
  const sectionFields: Record<WithdrawSecretarySectionKey, WithdrawSecretaryFieldKey[]> = {
    company: ['companyName'],
    companyRegistration: ['companyRegistrationNo'],
    companyAddress: ['companyAddress'],
    withdrawSecretary: ['secretaryName', 'secretaryIdentificationNo'],
    resignationDetails: ['secretaryResignationAddress', 'resignationDate'],
    resolution: ['resolutionDate'],
  };
  const names = sectionFields[section].flatMap((f) => getFieldDocNames(f));
  return Array.from(new Set(names));
}

function requestLookupForResigningSecretary() {
  emit('request-person-lookup', {
    fieldGroup: 'resigningSecretary',
    defaultRole: 'SECRETARY',
  });
}

watch(
  () => props.documents,
  () => {
    void fetchRepeatingSectionsFromDocuments();
  },
  { immediate: true, deep: true },
);

watch(
  () => model.value.repeatingData,
  () => {
    replaceRepeatingSectionMeta([
      ...repeatingSectionsMeta.value,
      ...deriveRepeatingSectionMetaFromRows(),
    ]);
  },
  { immediate: true, deep: true },
);

watch(
  withdrawSecretaryRepeatingSections,
  (sections) => {
    if (props.readOnly || sections.length === 0) return;
    const nextRepeatingData = { ...(model.value.repeatingData || {}) };
    let changed = false;

    for (const section of sections) {
      if (Array.isArray(nextRepeatingData[section.sectionName]) && nextRepeatingData[section.sectionName].length > 0) {
        continue;
      }
      nextRepeatingData[section.sectionName] = [buildInitialRepeatingRow(section)];
      changed = true;
    }

    if (changed) {
      model.value.repeatingData = nextRepeatingData;
      syncSecretaryFlatFieldsFromRepeatingRows();
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx || ctx.fieldGroup !== 'resigningSecretary') return;
    const fullName = person.name || person.contactName || '';
    const address = person.primaryAddress || '';
    const regNo = String((person as any).registrationIdNo || (person as any).contactRegNo || '').trim();
    if (fullName) model.value.secretaryName = fullName;
    if (regNo) model.value.secretaryIdentificationNo = regNo;
    if (address) model.value.secretaryResignationAddress = address;
    model.value.contactCd = String((person as any).contactCd || '').trim();
    model.value.positionCd = model.value.positionCd || 'B30033';
    model.value.contactData = {
      contactCd: String((person as any).contactCd || '').trim(),
      contactName: fullName,
      contactRegNo: regNo,
      dateOfBirth: String((person as any).dateOfBirth || '').trim(),
      nationalityText: String((person as any).nationalityName || (person as any).nationality || '').trim(),
      contactEmail: String((person as any).email || (person as any).contactEmail || '').trim(),
      mobileNo: String((person as any).mobileNo || '').trim(),
      primaryAddress: address,
      dateOfAppointment: String(
        (person as any).appointmentDate || (person as any).dateOfAppointment || '',
      ).trim(),
    };
    applySelectedPersonToRepeatingSection(person, ctx);
  },
);

const isLoadingReasons = ref(false);
const reasonOptions = ref<Array<{ title: string; value: string }>>([]);

async function fetchCessationReasons() {
  try {
    isLoadingReasons.value = true;
    const response = await baseCodeApi.getCodesByMainCode('SG', 'B12', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const codeMap = new Map<string, { title: string; value: string }>();

    codes.forEach((code: any) => {
      const totalCode = code.totalCode || code.TOTAL_CODE || '';
      const subCode = code.subCode || code.SUB_CODE || '';
      if (!totalCode || subCode === '000') return;

      if (!codeMap.has(totalCode)) {
        codeMap.set(totalCode, {
          title:
            code.codeName ||
            code.CODE_NAME ||
            code.defaultCodeName ||
            code.DEFAULT_CODE_NAME ||
            totalCode,
          value: totalCode,
        });
      }
    });

    reasonOptions.value = Array.from(codeMap.values());
  } catch (error) {
    console.error('Failed to fetch cessation reasons', error);
    reasonOptions.value = [];
  } finally {
    isLoadingReasons.value = false;
  }
}

watch(
  () => model.value.reasonForCessation,
  (newReason) => {
    if (newReason !== 'B12004') {
      model.value.otherReasonForCessation = '';
    }
  },
  { immediate: true },
);

const suggestedToday = computed(() => getCurrentDateByJurisdiction());

function formatIsoForSession(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return iso;
  return formatDateByUserFormat(d);
}

const dismissedSuggestions = ref<Set<string>>(new Set());
function suggestionKey(field: string, value: string): string {
  return `${field}:${value}`;
}
function isSuggestionDismissed(field: string, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}
function dismissSuggestion(field: string, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}

function applySuggestedToday(field: 'resignationDate' | 'resolutionDate') {
  const today = suggestedToday.value;
  if (!today) return;
  if (field === 'resignationDate') {
    model.value.resignationDate = today;
    resignation.dateValue.value = today;
    return;
  }
  model.value.resolutionDate = today;
  resolution.dateValue.value = today;
}

fetchCessationReasons();
</script>

<style scoped>
.wd-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.wd-section {
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px 0;
  overflow: hidden;
}

.wd-section > :not(.wd-section-header) {
  padding-left: 14px;
  padding-right: 14px;
}

.wd-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #283593;
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}

.wd-section-docs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-wrap: wrap;
}

.wd-section-docs-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
}

.wd-doc-chip {
  max-width: 240px;
}

.wd-doc-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wd-more-chip {
  font-size: 11px;
  color: #888;
}

.wd-repeating-wrap {
  padding: 0 2px;
}

.wd-repeating-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.wd-repeating-table + .wd-repeating-table {
  margin-top: 16px;
}

.wd-repeating-table :deep(.d-flex.align-center.ga-2.mb-3.flex-wrap > .v-btn:last-child) {
  display: none !important;
}

.wd-repeating-table :deep(.d-flex.align-center.ga-2.mt-2 > .v-btn:first-child) {
  display: none !important;
}

.wd-repeating-table :deep(.d-flex.align-center.ga-2.mt-2 > .v-spacer) {
  display: none !important;
}

.wd-suggest-chip {
  cursor: pointer;
  font-size: 11px !important;
  background: #ffffff !important;
  color: #212121 !important;
  border-color: #ffb300 !important;
  border-width: 1px !important;
}
.wd-suggest-chip:hover {
  background: #fff8e1 !important;
}
.wd-suggest-chip :deep(.v-chip__content) {
  color: #212121 !important;
}
.wd-suggest-chip :deep(.v-icon) {
  color: #ffb300 !important;
}
.wd-suggest-chip :deep(.v-chip__close) {
  font-size: 13px;
  margin-inline-start: 4px;
  opacity: 0.7;
  color: #757575 !important;
}
.wd-suggest-chip :deep(.v-chip__close:hover) {
  opacity: 1;
  color: #212121 !important;
}
</style>
